import * as React from "react";
import {
  AlertCircle,
  AlertTriangle,
  Server,
  Globe,
  Sliders,
  Cpu,
  ChevronDown,
  ChevronUp,
} from "react-feather";
import { HealthIssue, HEALTH_COLORS, WorkloadMessage } from "../utils";
import { InternalLink } from "../../../newcomponents/table/components/InternalLink";

interface SplitTagProps {
  label: string;
  value: string;
  truncate?: boolean;
}

const SimpleTag: React.FC<{ icon?: React.ReactNode; value: string }> = ({ icon, value }) => (
  <span
    className="health-tag inline-flex items-center gap-2 text-xs px-2 py-1 rounded flex-shrink-0"
  >
    {icon}
    {value}
  </span>
);

const SplitTag: React.FC<SplitTagProps> = ({ label, value, truncate }) => (
  <span
    className={`inline-flex items-center text-xs rounded overflow-hidden flex-shrink-0 ${truncate ? "max-w-[150px]" : ""}`}
    title={truncate ? `${label}=${value}` : undefined}
  >
    <span className="health-tag px-2 py-1">
      {label}
    </span>
    <span className={`health-tag-value px-2 py-1 ${truncate ? "truncate" : ""}`}>
      {value}
    </span>
  </span>
);

interface HealthIssueCardProps {
  issue: HealthIssue;
  isExpanded: boolean;
  onToggle: () => void;
}

export const HealthIssueCard: React.FC<HealthIssueCardProps> = ({ issue, isExpanded, onToggle }) => {
  const getSeverityIcon = (severity: HealthIssue["severity"]) => {
    switch (severity) {
      case "error":
        return <AlertCircle size={16} color={HEALTH_COLORS.error} />;
      case "warning":
        return <AlertTriangle size={16} color={HEALTH_COLORS.warning} />;
      default:
        return <AlertCircle size={16} color={HEALTH_COLORS.inactive} />;
    }
  };

  const getTypeIcon = (type: HealthIssue["type"]) => {
    switch (type) {
      case "workload":
        return <Server size={12} />;
      case "domain":
        return <Globe size={12} />;
      case "quota":
        return <Sliders size={12} />;
      case "agent":
        return <Cpu size={12} />;
      default:
        return null;
    }
  };

  const hasMessages = issue.messages && issue.messages.length > 0;

  return (
    <div
      className={`rounded-lg p-4 transition-all ${isExpanded ? "" : "cursor-pointer hover:shadow-md"}`}
      style={{
        backgroundColor: "var(--health-card-bg)",
        border: "1px solid var(--color-border)",
        gridColumn: isExpanded ? "span 4" : "span 2",
        maxHeight: isExpanded ? "none" : 120,
        overflow: "hidden",
      }}
      onClick={!isExpanded ? onToggle : undefined}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
          <div className="flex-shrink-0">{getSeverityIcon(issue.severity)}</div>
          <SimpleTag icon={getTypeIcon(issue.type)} value={issue.type} />
          {issue.workloadType && (
            <SimpleTag value={issue.workloadType} />
          )}
          {issue.gvc && (
            <SplitTag label="gvc" value={issue.gvc} truncate />
          )}
        </div>
        {isExpanded && (
          <button
            onClick={onToggle}
            className="p-1 rounded hover:bg-opacity-10 transition-colors flex-shrink-0"
            style={{ color: "var(--color-label)" }}
          >
            <ChevronUp size={16} />
          </button>
        )}
      </div>

      {/* Title */}
      <div
        className={`text-sm font-medium mb-1 ${isExpanded ? "" : "truncate"}`}
        style={{ color: "var(--color-default)" }}
        title={issue.title}
      >
        {issue.link ? <InternalLink label={issue.title} to={issue.link} /> : issue.title}
      </div>

      {/* Description */}
      <div
        className={`text-xs ${isExpanded ? "" : "truncate"}`}
        style={{ color: "var(--color-label)" }}
        title={issue.description}
      >
        {issue.description}
      </div>

      {/* Expanded Content - Messages */}
      {isExpanded && hasMessages && (
        <div className="mt-4 pt-4" style={{ borderTop: "1px dashed var(--color-border)" }}>
          <div className="text-xs font-medium mb-2" style={{ color: "var(--color-label)" }}>
            All Messages ({issue.messages!.length})
            {issue.expectedVersion && <span className="ml-2">| Expected Version: {issue.expectedVersion}</span>}
          </div>
          <div className="space-y-2">
            {issue.messages!.map((msg, index) => (
              <MessageItem key={index} message={msg} />
            ))}
          </div>
        </div>
      )}

      {/* Collapse hint when not expanded */}
      {!isExpanded && (
        <div className="flex items-center justify-center mt-2" style={{ color: "var(--color-label)" }}>
          <ChevronDown size={14} />
        </div>
      )}
    </div>
  );
};

interface MessageItemProps {
  message: WorkloadMessage;
}

const MessageItem: React.FC<MessageItemProps> = ({ message }) => {
  return (
    <div className="rounded p-2 text-xs" style={{ backgroundColor: "var(--color-border)" }}>
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span style={{ color: "var(--color-label)" }}>
          <strong>Location:</strong> {message.location}
        </span>
        {message.version !== undefined && (
          <span style={{ color: "var(--color-label)" }}>
            <strong>Version:</strong> {message.version}
          </span>
        )}
        {message.container && (
          <span style={{ color: "var(--color-label)" }}>
            <strong>Container:</strong> {message.container}
          </span>
        )}
      </div>
      <div style={{ color: "var(--color-default)" }}>{message.text}</div>
    </div>
  );
};
